import React, { useState, useCallback, useEffect } from 'react';
import { Layout } from 'antd';
import MessageHeader from './MessageHeader';
import MessageContent from './MessageContent';
import MessageDetailsDrawer from './MessageDetailsDrawer';
import MessageFooter from './MessageFooter/MessageFooter';
import MessageInputReplyPreview from './MessageInputReplyPreview/MessageInputReplyPreview';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import useMessageFetching from '../hooks/useMessageFetching';
import useContactStatus from '../hooks/useContactStatus';
import useDraftAttachments from '../hooks/useDraftAttachments';
import useMessageSeenTracking from '../hooks/useMessageSeenTracking';
import useSelectedMessageSync from '../hooks/useSelectedMessageSync';
import useMessageScrollManagement from '../hooks/useMessageScrollManagement';
import useMessageSocketEvents from '../hooks/useMessageSocketEvents';
import useContactSocketSync from '../hooks/useContactSocketSync';
import { sendMessage, deleteMessage } from '../MessagesServices';

const Messages = React.memo(({ selectedConversation, onBack, setConversations, setSelectedConversation, screens }) => {
  const { isDarkMode } = useTheme();
  const { handleError } = useErrorHandler();

  // ===== Local UI State =====
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  // ===== Custom Hooks =====
  const { draftAttachments, clearDrafts } = useDraftAttachments(selectedConversation);
  const { markMessageAsSeen } = useMessageSeenTracking(selectedConversation, setConversations);
  const { footerFocusRef, listScrollApiRef, scrollToBottom, focusFooterInput } = useMessageScrollManagement();

  const {
    messages,
    loading,
    hasMore,
    messageRefs,
    handleScroll,
    addMessage,
    updateMessage,
    deleteMessage: removeMessage,
    isInitialLoading,
    isFetchingMoreTop,
    isFetchingMoreBottom,
    fetchMessagesAroundMessage
  } = useMessageFetching(selectedConversation, handleError, markMessageAsSeen);

  const {
    isIgnored,
    isSnoozed,
    isAdmin,
    updateContactStatus
  } = useContactStatus(selectedConversation, setConversations, setSelectedConversation);

  // ===== Socket Synchronization =====
  useSelectedMessageSync(messages, selectedMessage, setSelectedMessage, drawerVisible, setDrawerVisible);
  useMessageSocketEvents(selectedConversation, messages, addMessage, updateMessage, removeMessage, markMessageAsSeen);
  useContactSocketSync(selectedConversation, setSelectedConversation, setConversations);

  // Reset reply preview when switching conversations
  useEffect(() => {
    setReplyTo(null);
  }, [selectedConversation?.id]);

  // ===== Event Handlers =====
  const handleReplyClick = useCallback((replyTo) => {
    if (!replyTo || !replyTo.id) {
      return;
    }

    const targetMessageId = replyTo.id;
    const targetMessage = messages.find(m => m.id === targetMessageId);

    if (targetMessage && messageRefs.current[targetMessageId]?.current) {
      // Message is already loaded, scroll to it
      messageRefs.current[targetMessageId].current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      setHighlightedMessageId(targetMessageId);
      setTimeout(() => setHighlightedMessageId(null), 2000);
    } else if (targetMessageId) {
      // Message found in memory but not loaded, fetch messages around it
      fetchMessagesAroundMessage(targetMessageId).then(() => {
        // Wait for render, then scroll to the message
        setTimeout(() => {
          if (messageRefs.current[targetMessageId]?.current) {
            messageRefs.current[targetMessageId].current.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            setHighlightedMessageId(targetMessageId);
            setTimeout(() => setHighlightedMessageId(null), 2000);
          }
        }, 200);
      }).catch((error) => {
        handleError(error);
      });
    }
  }, [messages, messageRefs, fetchMessagesAroundMessage, handleError]);


  const handleReplyAction = useCallback((messageItem) => {
    setReplyTo(messageItem);
    focusFooterInput();
  }, [focusFooterInput]);

  const handleSendMessage = useCallback(async (messageData) => {
    scrollToBottom();
    setIsSending(true);

    try {
      const payload = {
        attachment_ids: messageData.attachment_ids || [],
        reply_to_message_id: replyTo?.id || undefined
      };

      if (messageData.message && messageData.message.trim()) {
        payload.content = messageData.message;
      }

      await sendMessage(selectedConversation?.id, payload);

      clearDrafts();
      setReplyTo(null);
      scrollToBottom();
    } catch (error) {
      handleError(error);
    } finally {
      setIsSending(false);
    }
  }, [selectedConversation?.id, handleError, replyTo?.id, scrollToBottom, clearDrafts]);

  const handleDeleteMessage = useCallback(async (messageId) => {
    await deleteMessage(messageId);
  }, []);

  // ===== Styles =====
  const layoutStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    width: '100%',
    background: isDarkMode ? '#141414' : '#fff',
    overflow: 'hidden'
  };

  // ===== Render =====
  return (
    <Layout style={layoutStyle}>
      {/* Header */}
      <MessageHeader
        screens={screens}
        onBack={onBack}
        isIgnored={isIgnored}
        isSnoozed={isSnoozed}
        isAdmin={isAdmin}
        updateContactStatus={updateContactStatus}
        selectedConversation={selectedConversation}
      />

      {/* Message List */}
      <MessageContent
        screens={screens}
        selectedConversation={selectedConversation}
        messages={messages}
        loading={loading}
        isInitialLoading={isInitialLoading}
        isFetchingMoreTop={isFetchingMoreTop}
        isFetchingMoreBottom={isFetchingMoreBottom}
        hasMore={hasMore}
        messageRefs={messageRefs}
        handleScroll={handleScroll}
        handleReplyClick={handleReplyClick}
        onReplyAction={handleReplyAction}
        setSelectedMessage={setSelectedMessage}
        setDrawerVisible={setDrawerVisible}
        scrollApiRef={listScrollApiRef}
        highlightedMessageId={highlightedMessageId}
      />

      {/* Message Details Drawer */}
      <MessageDetailsDrawer
        screens={screens}
        drawerVisible={drawerVisible}
        selectedMessage={selectedMessage}
        setDrawerVisible={setDrawerVisible}
        setSelectedMessage={setSelectedMessage}
        onDeleteMessage={handleDeleteMessage}
      />

      {/* Reply Preview */}
      <MessageInputReplyPreview
        replyTo={replyTo}
        onCancel={() => setReplyTo(null)}
      />

      {/* Footer Input */}
      <MessageFooter
        onSendMessage={handleSendMessage}
        isSending={isSending}
        focusInputRef={footerFocusRef}
        conversationId={selectedConversation?.id}
        initialFileList={draftAttachments}
      />
    </Layout>
  );
}, (prevProps, nextProps) => (
  prevProps.selectedConversation?.id === nextProps.selectedConversation?.id &&
  prevProps.selectedConversation?.is_writing === nextProps.selectedConversation?.is_writing &&
  prevProps.selectedConversation?.processing_delay_applied_at === nextProps.selectedConversation?.processing_delay_applied_at &&
  prevProps.selectedConversation?.processing_delay_applied_seconds === nextProps.selectedConversation?.processing_delay_applied_seconds &&
  prevProps.onBack === nextProps.onBack &&
  prevProps.setConversations === nextProps.setConversations &&
  prevProps.setSelectedConversation === nextProps.setSelectedConversation &&
  prevProps.screens?.xs === nextProps.screens?.xs
));

export default Messages;
